import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, CheckCircle2, ArrowRight } from 'lucide-react';
import { sendFormEmail } from '../utils/sendFormEmail';
import { categories } from '../data/products';
import { getRegionMeta } from '../utils/region';
export function Contact() {
  const region = getRegionMeta();
  const addressPlaceholder = `123 Main St, ${region.cities[0]}, WA`;
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const form = e.target as HTMLFormElement;
      const materialSelect = form.querySelector('select[class*="appearance-none"]') as HTMLSelectElement;
      // Estimated Yardage is now a free-text input (was previously a
      // restrictive dropdown with a wrong "5 yd minimum"). Read it by name.
      const yardageInput = form.querySelector('input[name="estimatedYardage"]') as HTMLInputElement | null;
      const addressInput = form.querySelector('input[name="deliveryAddress"]') as HTMLInputElement;
      const rc = region.code;
      const brandName = `${rc} Gravel`;
      await sendFormEmail({
        subject: `${rc}Gravel — New Contact / Quote Request`,
        'First Name': formData.firstName,
        'Last Name': formData.lastName,
        email: formData.email,
        _replyto: formData.email,
        _autoresponse: `Hi ${formData.firstName}! Thank you for reaching out to ${brandName}! We've received your message and will get back to you shortly.\n\nIf you need anything sooner, call us at (206) 619-3742.\n\n- The ${brandName} Team`,
        Phone: formData.phone,
        Material: materialSelect?.value || '',
        'Estimated Yardage': yardageInput?.value || '',
        'Delivery Address': addressInput?.value || '',
        Message: formData.message
      });
      setIsSubmitted(true);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        message: ''
      });
      setTimeout(() => {
        formRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 100);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  // Dark mode keeps the deeper, stepped-down palette; light
  // mode reverts to the original warm-cream tones so the page
  // reads bright and friendly in daylight while still feeling
  // grounded after dark.
  const inputClass = 'w-full bg-[#f6f5f2] dark:bg-[#2d2c2b] border border-stone-300 dark:border-[#3e3d3d] rounded-xl px-4 py-3 text-sm text-brand-charcoal dark:text-white placeholder-brand-muted dark:placeholder-stone-600 focus:ring-2 focus:ring-brand-orange focus:border-transparent outline-none transition-all';
  return <main className="min-h-[calc(100vh-64px)] bg-[#faf9f7] dark:bg-[#2d2c2b] py-12 px-4 sm:px-6 lg:px-8 transition-colors duration-300 flex items-center justify-center" data-id="element-2228" data-mp="2mg38trfum6uu">
      <div className="max-w-6xl w-full mx-auto" data-id="element-2229" data-mp="3f4hb4jdyzpc7">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4
      }} className="mb-10 text-center lg:text-left" data-id="element-2230" data-mp="314q0msmnwr2y">
          <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-stone-900 dark:text-white mb-3 tracking-tight" data-id="element-2231" data-mp="1porje1hbyyqn">
            Contact & <span className="text-brand-orange" data-id="element-2232" data-mp="1tsfrcl9vjwcf">Quotes</span>
          </h1>
          <p className="text-stone-600 dark:text-stone-300 text-sm md:text-base max-w-2xl mx-auto lg:mx-0 font-sub" data-id="element-2233" data-mp="29z2id74y9mmp">
            Need a quote or have a question? Reach out directly or drop your
            project details below.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-2234" data-mp="1v94nk8cilyoj">
          {/* Left Column: Form */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} ref={formRef} className="lg:col-span-2 bg-[#fcfbfa] dark:bg-[#3a3939] rounded-2xl p-6 md:p-8 border border-stone-200 dark:border-[#4a4949] shadow-lg" data-id="element-2235" data-mp="2swmxvnltr8un">
            {isSubmitted ? <div className="h-full flex flex-col items-center justify-center text-center py-12" data-id="element-2236" data-mp="2ewkgs3lccjgg">
                <div className="w-16 h-16 bg-brand-orange/10 rounded-full flex items-center justify-center mb-4" data-id="element-2237" data-mp="17l3m1jdr7iim">
                  <CheckCircle2 className="w-8 h-8 text-brand-orange" data-id="element-2238" data-mp="13k1l8y46uyej" />
                </div>
                <h3 className="font-heading text-2xl font-bold text-stone-900 dark:text-white mb-2" data-id="element-2239" data-mp="3f75347ottp5a">
                  Request Received
                </h3>
                <p className="text-stone-600 dark:text-stone-300 mb-6 max-w-sm text-sm" data-id="element-2240" data-mp="3o5qgzpjcsfzq">
                  Thanks for reaching out. We'll get back to you shortly with
                  pricing and availability.
                </p>
                <button onClick={() => setIsSubmitted(false)} className="px-6 py-2.5 bg-brand-orange hover:bg-brand-orangeHover text-white rounded-none font-bold text-sm transition-all" data-id="element-2241" data-mp="1pvzlv8rbx37">
                  Send Another Request
                </button>
              </div> : <form onSubmit={handleSubmit} className="space-y-4" data-id="element-2242" data-mp="fqoy0r26jcbl">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2243" data-mp="fnszb0iicjm3">
                  <div data-id="element-2244" data-mp="2ck0aw5oquak1">
                    <label className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-2245" data-mp="2cktj7b3cp84a">
                      First Name *
                    </label>
                    <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} required className={inputClass} data-id="element-2246" data-mp="3kspn1od6j8w4" />
                  </div>
                  <div data-id="element-2247" data-mp="3tte0ohynv78h">
                    <label className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-2248" data-mp="3tup4uiw0qh04">
                      Last Name *
                    </label>
                    <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} required className={inputClass} data-id="element-2249" data-mp="7l4c2nwkn7jn" />
                  </div>
                </div>

                <div data-id="element-2250" data-mp="ger5w8h7y0q7">
                  <label className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-2251" data-mp="gd6cim6n4ntx">
                    Email Address *
                  </label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} required className={inputClass} data-id="element-2252" data-mp="qc5k01fo6dfc" />
                </div>

                <div data-id="element-2253" data-mp="lf5fhuygxiqc">
                  <label className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-2254" data-mp="lbhlyvt0wwbm">
                    Phone Number
                  </label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className={inputClass} data-id="element-2255" data-mp="19xp1x43z8d5b" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2256" data-mp="10017yge739o7">
                  <div data-id="element-2257" data-mp="10245rmlla73h">
                    <label className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-2258" data-mp="m8mibqps8a86">
                      Material Needed
                    </label>
                    <select className={inputClass + ' appearance-none'} data-id="element-2259" data-mp="r1fqypodg2pj">
                      <option value="" data-id="element-2260" data-mp="cc025f8ei7ch">Select a material...</option>
                      {categories.map(cat => <optgroup key={cat.name} label={cat.name} data-id="element-2261" data-mp="habb7nqvgdtn">
                          {cat.products.map(p => <option key={p.slug} value={p.name} data-id="element-2262" data-mp="3atjp0m6ymgl">
                              {p.name} — ${p.pricePerYard}/yd
                            </option>)}
                        </optgroup>)}
                      <option value="Not Sure Yet" data-id="element-2263" data-mp="npto1c43ina5">Not Sure Yet</option>
                    </select>
                  </div>
                  <div data-id="element-2264" data-mp="isbdxiece20k">
                    <label className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-2265" data-mp="ipxj9ankqrtj">
                      Estimated Yardage
                    </label>
                    <input type="text" name="estimatedYardage" className={inputClass} placeholder='e.g. "12 yards", "3 truckloads", "not sure"' data-id="element-2266" data-mp="8tcdgn3qdcon" />
                  </div>
                </div>

                <div data-id="element-2267" data-mp="3w53mma8s1ad6">
                  <label className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-2268" data-mp="3w4tsqbkm2j27">
                    Delivery Address *
                  </label>
                  <input type="text" name="deliveryAddress" required className={inputClass} placeholder={addressPlaceholder} data-id="element-2269" data-mp="3ma1xheei48al" />
                </div>

                <div data-id="element-2270" data-mp="3d82eigjxeu50">
                  <label className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-2271" data-mp="3d7kauyuxpdn2">
                    Message *
                  </label>
                  <textarea name="message" value={formData.message} onChange={handleChange} required rows={4} className={inputClass + ' resize-none'} data-id="element-2272" data-mp="1wrd17pcjsi40"></textarea>
                </div>

                <div className="pt-2" data-id="element-2273" data-mp="21odd4gw2wf31">
                  <button type="submit" disabled={isSubmitting} className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-3 bg-brand-button hover:bg-brand-buttonHover text-white rounded-none font-bold text-sm transition-all group disabled:opacity-60" data-id="element-2274" data-mp="26psvg16vg7hq">
                    {isSubmitting ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-2275" data-mp="1j088763oqo2n" /> : <>
                        Send Quote Request
                        <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-2276" data-mp="1nsrowbxo5zbo" />
                      </>}
                  </button>
                </div>
              </form>}
          </motion.div>

          {/* Right Column: Info */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="space-y-6" data-id="element-2277" data-mp="owkguho2g1yt">
            {/* Contact Info Card */}
            <div className="bg-[#fcfbfa] dark:bg-[#3a3939] text-stone-900 dark:text-white rounded-2xl p-6 border border-stone-200 dark:border-[#4a4949] shadow-lg" data-id="element-2278" data-mp="ytximh5f5gui">
              <h3 className="font-heading text-lg font-bold mb-5" data-id="element-2279" data-mp="68nt3aytw2ff">
                Get in Touch
              </h3>
              <div className="space-y-4" data-id="element-2280" data-mp="b6h1manlt8iv">
                <a href="tel:206-619-3742" className="flex items-center group" data-id="element-2281" data-mp="g4qv8gtok12d">
                  <div className="w-10 h-10 bg-brand-orange/10 rounded-xl flex items-center justify-center mr-3 group-hover:bg-brand-orange/20 transition-colors" data-id="element-2282" data-mp="kw0ixbvo4ubm">
                    <Phone className="w-4 h-4 text-brand-orange" data-id="element-2283" data-mp="3jlqvwwl959g3" />
                  </div>
                  <div data-id="element-2284" data-mp="3ojiyqlnfqfz2">
                    <p className="text-xs text-stone-500 dark:text-stone-400 mb-0.5" data-id="element-2285" data-mp="3oizeuze85hie">
                      Call Us
                    </p>
                    <p className="font-medium text-sm text-stone-900 dark:text-white" data-id="element-2286" data-mp="21nuh5ggpv0hc">
                      (206) 619-3742
                    </p>
                  </div>
                </a>
                <a href="mailto:info@206gravel.com" className="flex items-center group" data-id="element-2287" data-mp="1rqgcsf82i25e">
                  <div className="w-10 h-10 bg-brand-orange/10 rounded-xl flex items-center justify-center mr-3 group-hover:bg-brand-orange/20 transition-colors" data-id="element-2288" data-mp="1mqv4he70chg0">
                    <Mail className="w-4 h-4 text-brand-orange" data-id="element-2289" data-mp="1hz3p7ztaxy56" />
                  </div>
                  <div data-id="element-2290" data-mp="1dzba149ektqu">
                    <p className="text-xs text-stone-500 dark:text-stone-400 mb-0.5" data-id="element-2291" data-mp="1dwfe1lh48f51">
                      Email Us
                    </p>
                    <p className="font-medium text-sm text-stone-900 dark:text-white" data-id="element-2292" data-mp="140vjb2eafmhv">
                      info@206gravel.com
                    </p>
                  </div>
                </a>
                <div className="flex items-center" data-id="element-2293" data-mp="pddkulrbjo3w">
                  <div className="w-10 h-10 bg-brand-orange/10 rounded-xl flex items-center justify-center mr-3" data-id="element-2294" data-mp="pfztyptuybo8">
                    <MapPin className="w-4 h-4 text-brand-orange" data-id="element-2295" data-mp="uancm826kbuw" />
                  </div>
                  <div data-id="element-2296" data-mp="gfl7hn55qqj5">
                    <p className="text-xs text-stone-500 dark:text-stone-400 mb-0.5" data-id="element-2297" data-mp="kek6kr47jt2o">
                      Location
                    </p>
                    <p className="font-medium text-sm text-stone-900 dark:text-white" data-id="element-2298" data-mp="185pr3jl3f9jb">
                      Bonney Lake, WA
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Business Hours */}
            <div className="bg-[#fcfbfa] dark:bg-[#3a3939] rounded-2xl p-6 border border-stone-200 dark:border-[#4a4949] shadow-lg" data-id="element-2299" data-mp="13d698afdhs8p">
              <h3 className="font-heading text-lg font-bold text-stone-900 dark:text-white mb-4 flex items-center" data-id="element-2300" data-mp="1r2h63gtlr2hk">
                <Clock className="w-4 h-4 mr-2 text-brand-orange" data-id="element-2301" data-mp="1vva9a09fa2rj" />
                Hours
              </h3>
              <div className="space-y-3 text-sm" data-id="element-2302" data-mp="3gibaita7q5kp">
                <div className="flex justify-between items-center pb-2 border-b border-stone-200 dark:border-[#4a4949]" data-id="element-2303" data-mp="3bm2yhorbashr">
                  <span className="text-stone-600 dark:text-stone-300" data-id="element-2304" data-mp="3q9o35n3u4zrs">
                    Mon - Fri
                  </span>
                  <span className="font-medium text-stone-900 dark:text-white" data-id="element-2305" data-mp="3ld7dgogzmdcl">
                    6:00 AM – 5:00 PM
                  </span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-stone-200 dark:border-[#4a4949]" data-id="element-2306" data-mp="2snqpq44bbkmp">
                  <span className="text-stone-600 dark:text-stone-300" data-id="element-2307" data-mp="37l117dgygqif">
                    Saturday
                  </span>
                  <span className="font-medium text-stone-900 dark:text-white" data-id="element-2308" data-mp="mtgybd9oowhf">
                    7:00 AM – 3:00 PM
                  </span>
                </div>
                <div className="flex justify-between items-center" data-id="element-2309" data-mp="htw41g5glcb1">
                  <span className="text-stone-600 dark:text-stone-300" data-id="element-2310" data-mp="2njg2m25jzc8w">
                    Sunday
                  </span>
                  <span className="font-medium text-brand-orange" data-id="element-2311" data-mp="2siaxi2gsor9d">Closed</span>
                </div>
              </div>
            </div>

            {/* Service Area */}
            <div className="px-1 pt-2" data-id="element-2312" data-mp="31ipl58h8eig1">
              <h4 className="font-heading text-sm font-bold text-stone-900 dark:text-white mb-2" data-id="element-2313" data-mp="31jqzbltjnl1z">
                Service Area
              </h4>
              <p className="text-xs text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-2314" data-mp="3bg3oq7i23cqx">
                Proudly serving King and Pierce counties — Seattle, Bellevue,
                Kirkland, Redmond, Renton, Kent, Auburn, Federal Way, Tacoma,
                Puyallup, Lakewood, Bonney Lake, and beyond — with reliable,
                same-day and next-day delivery. Not sure if you're in our
                delivery zone? Give us a call — we're happy to help.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </main>;
}